import { createAction, handleActions } from "redux-actions";
import {
    getPromptPresetsAPI,
    getPromptPresetAPI,
    createPromptPresetAPI,
    updatePromptPresetAPI,
    updatePromptPresetStatusAPI,
    deletePromptPresetAPI,
} from "@modules/apis/prompt-presets";
import { AI_WORKSPACE_PRESET_TYPE, PROMPT_PRESET_RESPONSE_FORMAT_CODE } from "@constants";
import { getContentSchemaData, getContentSchemaSpec } from "@modules/content-schema";
import extractApiError from "@utils/extract-api-error";

// Action Types
const GET_PROMPT_PRESETS_PENDING = "GET_PROMPT_PRESETS_PENDING";
const GET_PROMPT_PRESETS_SUCCESS = "GET_PROMPT_PRESETS_SUCCESS";
const GET_PROMPT_PRESETS_FAILURE = "GET_PROMPT_PRESETS_FAILURE";

const CREATE_PROMPT_PRESET_SUCCESS = "CREATE_PROMPT_PRESET_SUCCESS";
const UPDATE_PROMPT_PRESET_DATA = "UPDATE_PROMPT_PRESET_DATA";
const UPDATE_PROMPT_PRESET_STATUS = "UPDATE_PROMPT_PRESET_STATUS";
const DELETE_PROMPT_PRESET_SUCCESS = "DELETE_PROMPT_PRESET_SUCCESS";

const RESET_PROMPT_PRESETS = "RESET_PROMPT_PRESETS";

// Initial State
const initialState = {
    pending: false,
    data: null,
    error: null,
    nextToken: null,
};

/**
 * 프리셋 responseFormat.jsonSchema에서 프리셋 종류(AI_WORKSPACE_PRESET_TYPE)를 꺼낸다.
 * 종류 키가 name → title로 바뀌었으므로(DAM-829) title을 우선 보고, 이전에 만든 프리셋·잡을 위해 name도 받는다. (DAM-1010)
 *
 * @param {{ title?: string, name?: string } | undefined} jsonSchema
 * @returns {string | undefined}
 */
export const getPresetSchemaType = (jsonSchema) => jsonSchema?.title ?? jsonSchema?.name;

// TIMELINE responseFormat 기본 구조
const TIMELINE_RESPONSE_FORMAT = {
    jsonSchema: {
        title: AI_WORKSPACE_PRESET_TYPE.VIDEO_HIGHLIGHT_EXTRACTION,
        schema: {
            type: "object",
            properties: {
                summary: {
                    type: "string",
                    description:
                        "A concise summary of the entire video content. Write the summary in the same language as the user's input. Do not translate, normalize, or change the language. Summarize the overall storyline or main events of the video, not a specific segment. Use 1–2 clear sentences in plain, factual language. Do not include timestamps, segment references, or detailed analysis. Example: '이 영상은 축구 경기에서 한 팀이 역전승을 거두는 과정을 보여줍니다.'",
                },
                segments: {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            startTime: {
                                type: "number",
                                description:
                                    "The start time of the video segment, expressed as the number of seconds from the beginning of the video. Use a numeric value only. For example, if the segment starts at 00:01:00, set this value to 60.",
                            },
                            endTime: {
                                type: "number",
                                description:
                                    "The end time of the video segment, expressed as the number of seconds from the beginning of the video. Use a numeric value only. This value must be greater than startTime.",
                            },
                            description: {
                                type: "string",
                                description:
                                    "A concise summary of the key event(s) that occur between startTime and endTime. Write the description in the same language as the user's input. Do not translate, paraphrase into another language, or change the original language. Use one clear, factual sentence describing only what is observable in this segment. Do not include timestamps, assumptions, or analysis. Example: 'A soccer player scores a goal.', '축구 선수가 골을 넣었습니다.'",
                            },
                            required: ["startTime", "endTime", "description"],
                        },
                    },
                },
            },
            required: ["summary", "segments"],
        },
    },
    example: {
        summary: "Overall video summary in the user's language.",
        segments: [
            {
                startTime: 0,
                endTime: 10,
                description: "Detailed description of the segment in the user's language.",
            },
        ],
    },
};

const TAG_RESPONSE_FORMAT = {
    jsonSchema: {
        title: AI_WORKSPACE_PRESET_TYPE.TAG_EXTRACTION,
        schema: {
            type: "object",
            properties: {
                tags: {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            value: {
                                type: "string",
                                description:
                                    "A single tag string extracted from the user's query, written in the same language as the user's input. This value must be a concise keyword or identifier with no spaces. Do not translate, normalize, or change the language of the tag. Do not include explanations, punctuation, or multiple words. Example values: 'news', '광고', '인터넷_강의'",
                            },
                            confidence: {
                                type: "number",
                                description:
                                    "A number representing the confidence level of the extracted tag. Use a value between 0 and 1, where higher values indicate higher confidence. Choose higher confidence only when the tag clearly and explicitly matches the user's intent.",
                            },
                            required: ["value", "confidence"],
                        },
                    },
                },
            },
            required: ["tags"],
        },
    },
    example: {
        tags: [
            {
                value: "Winning_Goal",
                confidence: 0.98,
            },
            {
                value: "Comeback_Victory",
                confidence: 0.95,
            },
        ],
    },
};

const DESCRIPTION_RESPONSE_FORMAT = {
    jsonSchema: {
        title: AI_WORKSPACE_PRESET_TYPE.DESCRIPTION_EXTRACTION,
        schema: {
            type: "object",
            properties: {
                summary: {
                    type: "string",
                    description:
                        "A comprehensive yet concise description of the entire video. This should be a well-structured paragraph (3-5 sentences) that covers the beginning, middle, and end of the video.",
                },
                headline: {
                    type: "string",
                    description:
                        "A single-sentence 'hook' or catchy title-like summary (under 100 characters) that represents the core value of the video. Example: '밤하늘의 별과 은하수'",
                },
            },
            required: ["summary", "headline"],
        },
    },
    example: {
        headline: "밤하늘의 별과 은하수",
        summary:
            "밤하늘의 별과 은하수가 아름답게 포착된 이 영상은 자연의 경이로움을 전달한다. 초기부터 끝까지 별들이 빛나는 장면이 이어지며, 특히 은하수가 별 사이로 흐르는 모습이 돋보인다. 영상은 천천히 밝아지면서 자연스럽게 마무리된다.",
    },
};

// 커스텀 메타데이터는 스키마를 임베드하지 않고 contentSchemaId로 참조만 전달
const getCustomMetadataResponseFormat = (contentSchemaId) => ({
    jsonSchema: {
        title: AI_WORKSPACE_PRESET_TYPE.CUSTOM_METADATA_EXTRACTION,
        contentSchemaId,
    },
});

export const convertResponseFormatCodeToResponseFormat = (code, contentSchemaId) => {
    switch (code) {
        case PROMPT_PRESET_RESPONSE_FORMAT_CODE.TIMELINE:
            return TIMELINE_RESPONSE_FORMAT;
        case PROMPT_PRESET_RESPONSE_FORMAT_CODE.TEXT:
            return null;
        case PROMPT_PRESET_RESPONSE_FORMAT_CODE.TAG:
            return TAG_RESPONSE_FORMAT;
        case PROMPT_PRESET_RESPONSE_FORMAT_CODE.DESCRIPTION:
            return DESCRIPTION_RESPONSE_FORMAT;
        case PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA:
            return getCustomMetadataResponseFormat(contentSchemaId);
        default:
            return null;
    }
};

export const convertResponseFormatToResponseFormatCode = (format) => {
    const presetType = getPresetSchemaType(format?.jsonSchema);
    if (presetType === AI_WORKSPACE_PRESET_TYPE.VIDEO_HIGHLIGHT_EXTRACTION) {
        return PROMPT_PRESET_RESPONSE_FORMAT_CODE.TIMELINE;
    }
    if (presetType === AI_WORKSPACE_PRESET_TYPE.TAG_EXTRACTION) {
        return PROMPT_PRESET_RESPONSE_FORMAT_CODE.TAG;
    }
    if (presetType === AI_WORKSPACE_PRESET_TYPE.DESCRIPTION_EXTRACTION) {
        return PROMPT_PRESET_RESPONSE_FORMAT_CODE.DESCRIPTION;
    }
    if (presetType === AI_WORKSPACE_PRESET_TYPE.CUSTOM_METADATA_EXTRACTION) {
        return PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA;
    }
    if (format === null) return PROMPT_PRESET_RESPONSE_FORMAT_CODE.TEXT;
    return "";
};

const parsePresets = (presets) => {
    return presets.map((preset) => ({
        ...preset,
        responseFormatCode: convertResponseFormatToResponseFormatCode(preset.responseFormat),
    }));
};

export default handleActions(
    {
        [GET_PROMPT_PRESETS_PENDING]: (state, action) => {
            return {
                ...initialState,
                pending: true,
                data: action.payload ? state.data : [],
            };
        },
        [GET_PROMPT_PRESETS_SUCCESS]: (state, action) => {
            return {
                ...state,
                pending: false,
                data: action.payload.data,
                nextToken: action.payload.nextToken,
            };
        },
        [GET_PROMPT_PRESETS_FAILURE]: (state, action) => {
            return {
                ...state,
                pending: false,
                error: extractApiError(action.payload),
            };
        },
        [CREATE_PROMPT_PRESET_SUCCESS]: (state, action) => {
            const newPreset = {
                ...action.payload,
                responseFormatCode: convertResponseFormatToResponseFormatCode(action.payload.responseFormat),
            };
            return {
                ...state,
                data: [newPreset, ...state.data],
            };
        },
        [UPDATE_PROMPT_PRESET_DATA]: (state, action) => {
            const updatedPreset = {
                ...action.payload,
                responseFormatCode: convertResponseFormatToResponseFormatCode(action.payload.responseFormat),
            };
            return {
                ...state,
                data: state.data.map((preset) => (preset.id === updatedPreset.id ? updatedPreset : preset)),
            };
        },
        [UPDATE_PROMPT_PRESET_STATUS]: (state, action) => {
            return {
                ...state,
                data: state.data.map((preset) =>
                    preset.id === action.payload.id
                        ? {
                              ...preset,
                              status: action.payload.value,
                          }
                        : preset,
                ),
            };
        },
        [DELETE_PROMPT_PRESET_SUCCESS]: (state, action) => {
            const { id } = action.payload;
            return {
                ...state,
                data: state.data.filter((preset) => preset.id !== id),
            };
        },
        [RESET_PROMPT_PRESETS]: () => {
            return initialState;
        },
    },
    initialState,
);

// Actions
export const resetPromptPresets = () => ({ type: RESET_PROMPT_PRESETS });
export const updatePromptPresetData = createAction(UPDATE_PROMPT_PRESET_DATA);

// Dispatch Functions
// dispatch(fetchPresetContentSchemaDetail({ contentSchemaId, projectId }))로 호출해 결과를 반환받는다.
export const fetchPresetContentSchemaDetail =
    ({ contentSchemaId, projectId }) =>
    (dispatch) => {
        if (!contentSchemaId) {
            return Promise.resolve(null);
        }
        return dispatch(getContentSchemaData(contentSchemaId, projectId))
            .then((raw) => (raw?.id ? raw : null)) // 조회 실패 시 getContentSchemaData가 error를 반환하므로 id로 유효성 확인
            .catch(() => null);
    };

export const getPromptPresets =
    ({ projectId, params = {} }) =>
    async (dispatch, getState) => {
        const { stage, promptPresets } = getState();

        dispatch({ type: GET_PROMPT_PRESETS_PENDING, payload: !!params.nextToken });

        try {
            const response = await getPromptPresetsAPI(stage.endpoint, projectId, params);
            const parsedData = parsePresets(response.data?.results || []);

            dispatch({
                type: GET_PROMPT_PRESETS_SUCCESS,
                payload: {
                    data: params.nextToken ? [...promptPresets.data, ...parsedData] : parsedData,
                    nextToken: response.data.nextToken ?? null,
                },
            });

            return response;
        } catch (error) {
            dispatch({
                type: GET_PROMPT_PRESETS_FAILURE,
                payload: error,
            });
            throw error;
        }
    };

export const createPromptPreset =
    ({ projectId, data }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const { responseFormatCode, contentSchemaId, ...rest } = data;

        const requestData = {
            ...rest,
            responseFormat: convertResponseFormatCodeToResponseFormat(responseFormatCode, contentSchemaId),
        };
        delete requestData.responseFormatCode;

        const response = await createPromptPresetAPI(stage.endpoint, projectId, requestData);

        dispatch({
            type: CREATE_PROMPT_PRESET_SUCCESS,
            payload: response.data,
        });

        return response;
    };

// 원본 프리셋을 조회해 같은 내용으로 새 프리셋을 만든다.
// 커스텀 메타데이터는 contentSchemaId 참조만 넘기면 되므로 content schema 상세는 조회하지 않는다.
export const duplicatePromptPreset =
    ({ projectId, id, name }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const response = await getPromptPresetAPI(stage.endpoint, projectId, id);
        const { description, prompt, responseFormat } = response.data;

        return dispatch(
            createPromptPreset({
                projectId,
                data: {
                    name,
                    description,
                    prompt,
                    responseFormatCode: convertResponseFormatToResponseFormatCode(responseFormat),
                    contentSchemaId: responseFormat?.jsonSchema?.contentSchemaId,
                },
            }),
        );
    };

export const updatePromptPresetDetail =
    ({ projectId, id, data }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const { responseFormatCode, contentSchemaId, ...updatedFields } = data;
        const requestData = { ...updatedFields };
        let responseFormat;

        if (responseFormatCode) {
            responseFormat = convertResponseFormatCodeToResponseFormat(responseFormatCode, contentSchemaId);
            requestData.responseFormat = responseFormat;
        }

        const response = await updatePromptPresetAPI(stage.endpoint, projectId, id, requestData);

        // 응답형태를 변경한 경우에만, 읽기 카드가 참조하는 content schema 상세도 함께 갱신한다 (커스텀=조회 / 그 외=null)
        const responseFormatPatch = {};
        if (responseFormatCode) {
            responseFormatPatch.responseFormat = responseFormat;
            responseFormatPatch.responseFormatCode = responseFormatCode;
            responseFormatPatch.contentSchemaDetail = null;

            if (responseFormatCode === PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA) {
                responseFormatPatch.contentSchemaDetail = await dispatch(
                    fetchPresetContentSchemaDetail({ contentSchemaId, projectId }),
                );
            }
        }

        return { response, updatedFields, responseFormatPatch };
    };

export const updatePromptPreset =
    ({ projectId, id, data }) =>
    async (dispatch) => {
        const { response } = await dispatch(updatePromptPresetDetail({ projectId, id, data }));

        dispatch({
            type: UPDATE_PROMPT_PRESET_DATA,
            payload: response.data,
        });

        return response;
    };

export const updatePromptPresetStatus =
    ({ projectId, id, data }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const response = await updatePromptPresetStatusAPI(stage.endpoint, projectId, id, data);

        dispatch({
            type: UPDATE_PROMPT_PRESET_STATUS,
            payload: { id, value: data.value },
        });

        return response;
    };

export const deletePromptPreset =
    ({ projectId, id }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const response = await deletePromptPresetAPI(stage.endpoint, projectId, id);

        dispatch({
            type: DELETE_PROMPT_PRESET_SUCCESS,
            payload: { id },
        });

        return response;
    };

// 상세 표시용 프리셋 조회 — 커스텀 메타데이터는 읽기 카드가 스키마 필드를 렌더하므로 content schema 상세까지 함께 조회한다.
export const getPromptPresetDetailWithContentSchema =
    ({ projectId, id }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const response = await getPromptPresetAPI(stage.endpoint, projectId, id);
        const responseFormat = response.data?.responseFormat;

        const data = {
            ...response.data,
            responseFormatCode: convertResponseFormatToResponseFormatCode(responseFormat),
            contentSchemaDetail: null,
        };

        if (data.responseFormatCode === PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA) {
            data.contentSchemaDetail = await dispatch(
                fetchPresetContentSchemaDetail({
                    contentSchemaId: responseFormat?.jsonSchema?.contentSchemaId,
                    projectId,
                }),
            );
        }

        return data;
    };

export const getPromptPresetDetail =
    ({ projectId, id }) =>
    async (dispatch, getState) => {
        const { project, stage } = getState();
        const response = await getPromptPresetAPI(stage.endpoint, project.id, id);
        const payloadData = { ...response.data };
        const responseFormat = response.data?.responseFormat;
        payloadData.responseFormatCode = convertResponseFormatToResponseFormatCode(responseFormat);
        payloadData.contentSchemaDetail = null;

        if (payloadData.responseFormatCode === PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA) {
            payloadData.contentSchemaDetail = await dispatch(
                fetchPresetContentSchemaDetail({
                    contentSchemaId: responseFormat?.jsonSchema?.contentSchemaId,
                    projectId: project.id,
                }),
            );
        }
        return payloadData;
    };

// 잡 생성용 프리셋 조회 — 커스텀 메타데이터는 contentSchemaId 참조만 저장하므로, 실제 JSON Schema를
// 조회해 responseFormat.jsonSchema를 { title, schema }로 치환해 반환한다.
export const getPromptPresetForJob =
    ({ projectId, id }) =>
    async (dispatch, getState) => {
        const { stage } = getState();

        const response = await getPromptPresetAPI(stage.endpoint, projectId, id);
        const { responseFormat } = response.data;

        if (getPresetSchemaType(responseFormat?.jsonSchema) !== AI_WORKSPACE_PRESET_TYPE.CUSTOM_METADATA_EXTRACTION) {
            return response;
        }

        const schemaSpec = await dispatch(getContentSchemaSpec(responseFormat.jsonSchema.contentSchemaId, projectId));
        // 스키마 스펙이 비어 있으면 실제 스키마 없이 잡이 생성되는 것을 막는다.
        if (!schemaSpec) {
            throw new Error("Empty content schema spec");
        }
        // 스펙 응답의 draft-07 메타($schema, title)와 중복 name은 잡 스키마에서 제외한다.
        const { $schema: _$schema, name: _name, title: _title, ...schema } = schemaSpec;
        return {
            ...response,
            data: {
                ...response.data,
                responseFormat: {
                    ...responseFormat,
                    contentSchemaId: responseFormat.jsonSchema.contentSchemaId,
                    jsonSchema: {
                        title: getPresetSchemaType(responseFormat.jsonSchema),
                        schema,
                    },
                },
            },
        };
    };
